/**
 * LICITARS 3.0 - Process List V3
 * Process list with Navy/Silver theme
 * Based on licitars-3-0/ProcessList.jsx with functional backend
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Filter, FileText, Calendar, DollarSign, User, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { processApi } from "@/services/api";
import { Process } from "@/types/process";
import { getProcessStatusColor, getProcessStatusLabel } from "@/constants/process-status";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ProcessListV3() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalityFilter, setModalityFilter] = useState("all");

  // Fetch processes from backend
  const { data: processResponse, isLoading } = useQuery({
    queryKey: ["processes"],
    queryFn: async () => {
      const response = await processApi.list();
      // Handle both array and paginated response
      if (Array.isArray(response)) {
        return { results: response, count: response.length };
      }
      return response;
    },
  });

  const allProcesses = processResponse?.results || [];

  // Filter processes
  const filteredProcesses = allProcesses.filter((process) => {
    const matchesSearch =
      !searchTerm ||
      process.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.object?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || process.status?.toLowerCase() === statusFilter.toLowerCase();

    const matchesModality =
      modalityFilter === "all" ||
      process.modality?.name?.toLowerCase() === modalityFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesModality;
  });

  // Get unique modalities for filter
  const modalities = Array.from(
    new Set(allProcesses.map((p) => p.modality?.name).filter(Boolean))
  );

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "aberto" || statusLower === "active") return "bg-emerald-500/20 text-emerald-400 border-emerald-500";
    if (statusLower === "em_andamento" || statusLower === "in_progress") return "bg-blue-500/20 text-blue-400 border-blue-500";
    if (statusLower === "concluido" || statusLower === "completed") return "bg-purple-500/20 text-purple-400 border-purple-500";
    if (statusLower === "arquivado" || statusLower === "archived") return "bg-slate-500/20 text-slate-400 border-slate-500";
    if (statusLower === "cancelado" || statusLower === "cancelled") return "bg-red-500/20 text-red-400 border-red-500";
    return "bg-steel-500/20 text-steel-400 border-steel-500";
  };

  const getStatusLabel = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "aberto" || statusLower === "active") return "Aberto";
    if (statusLower === "em_andamento" || statusLower === "in_progress") return "Em Andamento";
    if (statusLower === "concluido" || statusLower === "completed") return "Concluído";
    if (statusLower === "arquivado" || statusLower === "archived") return "Arquivado";
    if (statusLower === "cancelado" || statusLower === "cancelled") return "Cancelado";
    return status;
  };

  const formatCurrency = (value: string | number) => {
    if (!value) return "R$ 0,00";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numValue);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR");
    } catch {
      return dateString;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-navy-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Processos Licitatórios</h1>
            <p className="text-muted-foreground">
              Gerencie e acompanhe todos os processos de licitação
            </p>
          </div>
          <Button
            onClick={() => navigate("/processes/new")}
            className="gradient-primary shadow-accent"
          >
            <Plus className="w-5 h-5 mr-2" />
            Novo Processo
          </Button>
        </div>

        {/* Filters */}
        <Card className="card-navy-glass border-navy-500">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por número, objeto ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-navy-800 border-navy-500 text-foreground placeholder:text-muted-foreground"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-navy-800 border-navy-500 text-foreground">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-navy-600 border-navy-500">
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                  <SelectItem value="arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>

              {/* Modality Filter */}
              <Select value={modalityFilter} onValueChange={setModalityFilter}>
                <SelectTrigger className="bg-navy-800 border-navy-500 text-foreground">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Modalidade" />
                </SelectTrigger>
                <SelectContent className="bg-navy-600 border-navy-500">
                  <SelectItem value="all">Todas as Modalidades</SelectItem>
                  {modalities.map((modality) => (
                    <SelectItem key={modality} value={modality.toLowerCase()}>
                      {modality}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Results Count */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {filteredProcesses.length} {filteredProcesses.length === 1 ? "processo encontrado" : "processos encontrados"}
              </p>
              {(searchTerm || statusFilter !== "all" || modalityFilter !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setModalityFilter("all");
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredProcesses.length === 0 && (
          <Card className="card-navy-glass border-navy-500">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {searchTerm || statusFilter !== "all" || modalityFilter !== "all"
                  ? "Nenhum processo encontrado"
                  : "Nenhum processo cadastrado"}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter !== "all" || modalityFilter !== "all"
                  ? "Tente ajustar os filtros de busca"
                  : "Comece criando seu primeiro processo licitatório"}
              </p>
              {!searchTerm && statusFilter === "all" && modalityFilter === "all" && (
                <Button
                  onClick={() => navigate("/processes/new")}
                  className="gradient-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Processo
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Process Cards */}
        {!isLoading && filteredProcesses.length > 0 && (
          <div className="space-y-4">
            {filteredProcesses.map((process) => (
              <Card
                key={process.id}
                className="card-navy-glass border-navy-500 hover:border-primary transition-all duration-300 cursor-pointer group"
                onClick={() => navigate(`/processes/${process.id}`)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-900/30 group-hover:shadow-blue-900/50 transition-shadow">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                              {process.object || 'Processo sem título'}
                            </h3>
                            <Badge className={`border ${getStatusColor(process.status)}`}>
                              {getStatusLabel(process.status)}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                            {process.code && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <FileText className="w-4 h-4" />
                                <span>{process.code}</span>
                              </div>
                            )}
                            {process.modality?.name && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <span className="font-medium">{process.modality.name}</span>
                              </div>
                            )}
                            {process.estimated_value && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <DollarSign className="w-4 h-4" />
                                <span>{formatCurrency(process.estimated_value)}</span>
                              </div>
                            )}
                            {process.responsible && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <User className="w-4 h-4" />
                                <span className="truncate">{process.responsible}</span>
                              </div>
                            )}
                          </div>
                          {process.description && (
                            <div className="mt-3 text-sm text-muted-foreground line-clamp-2">
                              {process.description}
                            </div>
                          )}
                          {(process.publication_date || process.opening_date) && (
                            <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                              {process.publication_date && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>Publicação: {formatDate(process.publication_date)}</span>
                                </div>
                              )}
                              {process.opening_date && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>Abertura: {formatDate(process.opening_date)}{process.opening_time && ` às ${process.opening_time}`}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
